import { useEffect, useState } from 'react';

import { useSelector, useDispatch } from 'react-redux';

import {
  Modal,
  ModalBody,
  ModalFooter,
  UserInfoForm,
  ModalHeader,
  Button,
} from 'components';
import {
  checkUserInfo,
  fetchPaymentInfoStart,
  updateInCompleteUserInfoByModal,
} from 'redux/payments/payments.actions';
import ApiLoader from 'components/ApiLoader';
import useTranslation from 'hooks/useTranslation';
import InformationPreview from './components/InformationPreview';
import { convertPersianDigitsToEnglishDigits } from 'utils/text';
import {
  getNotifMsg,
  isValidNationalCode,
  isValidNationalIdentifier,
  isValidPostalCode,
} from 'utils';
import { isValidIranMobileNum } from 'utils/validation';
import styles from './styles.module.scss';
import MandatoryBadge from './components/MandatoryBadge';
import { MobileVerificationModal } from 'components';
import { sendVerificationSmsAction } from 'redux/user/user.actions';
import { changeOperatorMobileNumber } from 'redux/operators/operators.actions';

const getPaymentInfo = (state) => state.paymentsData.paymentInfo;
const getProfileInfo = (state) => state.userData.profile;
const getAppLocale = (state) => state.appData.locale;
// mobile number entered during signup process
const getProfileMobileNum = ({ userData }) => userData.profile.mobile;

const SUBMIT_FORM_MODE = 'submit';
const CHECK_FORM_MODE = 'check';

function UserInfoModal({
  visible,
  handleClose,
  doAfterSuccessfullUpdate = () => {},
  showCancelBtn = true,
  showMandatoryBadge = false,
}) {
  const mobileNumValue = useSelector(getProfileMobileNum);
  const locale = useSelector(getAppLocale);

  const INITIAL_FORM_DATA = {
    selectedForm: 'person',
    person: {
      loadingSubmit: false,
      checkedData: '',
      fields: [
        {
          label: 'موبایل',
          type: 'text',
          placeholder: '+989123456789 مثال',
          id: 'mobileNumber',
          disabled: !!mobileNumValue,
          onChange: handleChangeInput,
          value: mobileNumValue,
          forced: !mobileNumValue,
          error: '',
          specialValidators: [
            {
              func: isValidIranMobileNum,
              errorMsg: getNotifMsg('INVALID_MOBILE', locale),
            },
          ],
        },
        {
          label: 'کدملی',
          id: 'id',
          type: 'text',
          placeholder: 'وارد کنید',
          onChange: handleChangeInput,
          forced: true,
          error: '',
          specialValidators: [
            {
              func: isValidNationalCode,
              errorMsg: getNotifMsg('INVALID_NATIONAL_ID_NUMBER', locale),
            },
          ],
        },
        {
          label: 'کدپستی',
          id: 'residencePostalCode',
          type: 'text',
          placeholder: 'وارد کنید',
          onChange: handleChangeInput,
          forced: true,
          error: '',
          specialValidators: [
            {
              func: (value) => value.length === 10 && isValidPostalCode(value),
              errorMsg: getNotifMsg('INVALID_POSTAL_CODE', locale),
            },
          ],
        },
        {
          label: 'تاریخ تولد',
          id: 'birthDate',
          type: 'datePicker',
          forced: true,
          error: '',
        },
        {
          id: 'name',
          label: 'نام',
          value: '',
          isPreview: true,
          error: '',
        },
        {
          id: 'family',
          label: 'نام خانوادگی',
          value: '',
          isPreview: true,
          error: '',
        },
        {
          id: 'residenceCity',
          label: 'شهر',
          value: '',
          isPreview: true,
          error: '',
        },
        {
          id: 'residenceProvince',
          label: 'استان',
          value: '',
          isPreview: true,
          error: '',
        },
        {
          id: 'residenceAddress',
          label: 'آدرس',
          value: '',
          isPreview: true,
          error: '',
        },
      ],
    },
    company: {
      loadingSubmit: false,
      checkedData: '',
      fields: [
        {
          label: 'موبایل',
          type: 'text',
          placeholder: '+989123456789 مثال',
          id: 'mobileNumber',
          disabled: !!mobileNumValue,
          onChange: handleChangeInput,
          value: mobileNumValue,
          forced: !mobileNumValue,
          error: '',
          specialValidators: [
            {
              func: isValidIranMobileNum,
              errorMsg: getNotifMsg('INVALID_MOBILE', locale),
            },
          ],
        },
        {
          label: 'شناسه ملی',
          id: 'nationalNumber',
          type: 'text',
          placeholder: 'وارد کنید',
          onChange: handleChangeInput,
          forced: true,
          error: '',
          specialValidators: [
            {
              func: isValidNationalIdentifier,
              errorMsg: getNotifMsg('COMPANY_INVALID_NATIONAL', locale),
            },
          ],
        },
        {
          id: 'phone',
          label: 'تلفن دفتر',
          value: '',
          isPreview: true,
          error: '',
        },
        {
          id: 'companyName',
          label: 'نام شرکت',
          value: '',
          isPreview: true,
          forced: true,
          error: '',
        },
        {
          id: 'address',
          label: 'آدرس',
          value: '',
          isPreview: true,
          error: '',
          forced: true,
        },
        {
          id: 'city',
          label: 'شهر',
          value: '',
          isPreview: true,
          error: '',
          forced: true,
        },
        {
          id: 'province',
          label: 'استان',
          value: '',
          isPreview: true,
          error: '',
          forced: true,
        },
        {
          id: 'postalCode',
          label: 'کدپستی',
          value: '',
          isPreview: true,
          forced: true,
          error: '',
        },
      ],
    },
  };

  const [showMobileVerfifyModal, setShowMobileVerifyModal] = useState(false);
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const showPreviewSection = !!formData[formData.selectedForm].checkedData;
  const paymentInfo = useSelector(getPaymentInfo);
  const profile = useSelector(getProfileInfo);
  const dispatch = useDispatch();
  const { getTranslation } = useTranslation('COMMON');
  const { t } = getTranslation();

  const inputMobileNumber = formData[formData.selectedForm]?.fields.find(
    (f) => f.id === 'mobileNumber'
  )?.value;

  function handleChangeInput(e) {
    const { name, value } = e.target;
    // converting persian digits to eng digits for better UX
    const valueWithConvertedDigits = convertPersianDigitsToEnglishDigits(value);
    const { selectedForm } = formData;

    const newFields = formData[selectedForm].fields.map((item) => {
      if (item.id === name) {
        return {
          ...item,
          value: valueWithConvertedDigits,
        };
      }

      return item;
    });

    setFormData((prev) => ({
      ...prev,
      [selectedForm]: {
        ...prev[selectedForm],
        fields: newFields,
      },
    }));
  }

  const paymentInfoData = paymentInfo.data;
  const loadingFetchInfo = paymentInfo.loading && !paymentInfoData;
  const isPaymentInfoEmpty =
    !paymentInfoData || Object.keys(paymentInfoData).length === 0;

  // this function compares forced fields and if those fields are not filled or are not the same the form should get checked
  const needCheckFormData = () => {
    const { selectedForm } = formData;
    const { checkedData } = formData[selectedForm];

    // 'notCompare' is set when we want to skip the check process and directly save entered data. it is set when `check API` is broken
    if (formData[selectedForm].notCompare) return false;

    return (
      !formData[selectedForm] ||
      formData[selectedForm].fields.some(
        (field) =>
          field.id !== 'mobileNumber' && checkedData[field.id] !== field.value
      )
    );
  };

  const formMode = needCheckFormData() ? CHECK_FORM_MODE : SUBMIT_FORM_MODE;

  /**
   * This function checks for errors of each field and updates text error of it
   *
   * @param {Object} fields
   * @param {boolean} checkPreviewFields - whether check for preview fields or not
   * @returns {Object[]} - returns new fields
   */
  const checkForErrors = (fields, checkPreviewFields) => {
    const newFields = fields.map((item) => {
      let error;
      const needCheck = !checkPreviewFields ? !item.isPreview : true;

      if (needCheck && item.forced && !item.value) {
        error = 'پر کردن این فیلد الزامیست';
      }

      if (needCheck && !error && item.specialValidators) {
        for (let validator of item.specialValidators) {
          if (!validator.func(item.value)) {
            error = validator.errorMsg;
          }
        }
      }

      return {
        ...item,
        error,
      };
    });

    return newFields;
  };

  const setNewFields = (newFields, form) => {
    setFormData((prev) => ({
      ...prev,
      [form]: {
        ...prev[form],
        fields: newFields,
      },
    }));
  };

  const setLoadingForm = (status, formType) => {
    setFormData((prev) => ({
      ...prev,
      [formType]: {
        ...prev[formType],
        loadingSubmit: status,
      },
    }));
  };

  const getDataReducedData = () =>
    formData[formData.selectedForm].fields.reduce((prev, current) => {
      return {
        ...prev,
        [current.id]: current.value,
      };
    }, {});

  const onSuccessfullySendSms = () => {
    setShowMobileVerifyModal(true);
  };

  const sendSmsAndShowVerifyModal = () => {
    dispatch(
      sendVerificationSmsAction({
        mobile: inputMobileNumber,
        onSuccess: onSuccessfullySendSms,
      })
    );
  };

  const saveData = () => {
    const newFields = checkForErrors(
      formData[formData.selectedForm].fields,
      true
    );

    setNewFields(newFields, formData.selectedForm);
    const hasError = newFields.some((item) => !!item.error);

    if (hasError) return;

    setLoadingForm(true, formData.selectedForm);

    dispatch(
      updateInCompleteUserInfoByModal({
        values: getDataReducedData(),
        selectedForm: formData.selectedForm,
        handleToggle: handleClose,
        accountMobile: profile.mobile,
        setLoadingSubmit: setLoadingForm,
        doAfterSuccessfullUpdate,
      })
    );
  };

  const onSuccessfullyChangeOperatorNumber = () => {
    setShowMobileVerifyModal(false);

    saveData();
  };

  const changeMobileNumberIfEnteredCodeIsValid = () => {
    dispatch(
      changeOperatorMobileNumber({
        mobile: inputMobileNumber,
        operatorId: profile.userId,
        onSuccess: onSuccessfullyChangeOperatorNumber,
      })
    );
  };

  const handleSaveBtnClick = () => {
    // need to verify mobile number before saving data
    if (!mobileNumValue) {
      sendSmsAndShowVerifyModal();
    } else {
      saveData();
    }
  };

  const handleCheckButtonClick = () => {
    const newFields = checkForErrors(
      formData[formData.selectedForm].fields,
      false
    );

    setNewFields(newFields, formData.selectedForm);
    const hasError = newFields.some((item) => !!item.error);

    // do not proceed if error happened
    if (hasError) return;

    setLoadingForm(true, formData.selectedForm);

    dispatch(
      checkUserInfo({
        data: { ...getDataReducedData(), selectedForm: formData.selectedForm },
        setData: mapFetchedDataToFields,
        setLoading: setLoadingForm,
      })
    );
  };

  const handleCloseMobileVerifyModal = () => {
    setShowMobileVerifyModal(false);
  };

  const handleFormSelection = (formType) => {
    setFormData({ ...formData, selectedForm: formType });
  };

  const renderFooterContent = () => {
    const label =
      formMode === SUBMIT_FORM_MODE
        ? t('USER_INFO_MODAL').SUBMIT_BTN
        : t('USER_INFO_MODAL').CHECK_BTN;
    const func =
      formMode === SUBMIT_FORM_MODE
        ? handleSaveBtnClick
        : handleCheckButtonClick;
    const type = formMode === CHECK_FORM_MODE ? 'outline' : 'primary';
    const isLoading = formData[formData.selectedForm].loadingSubmit;

    return (
      <Button type={type} onClick={func} isLoading={isLoading}>
        {label}
      </Button>
    );
  };

  const renderModalContent = () => {
    if (loadingFetchInfo) {
      return (
        <div className={styles.api_loader_wrapper}>
          <ApiLoader />
        </div>
      );
    }

    return (
      <div className='user_info_form'>
        <ModalBody>
          <UserInfoForm
            formData={formData}
            selectedForm={formData.selectedForm}
            {...{
              handleFormSelection,
              showMandatoryBadge,
            }}
            handleChange={handleChangeInput}
          />

          {/* preview */}
          {showPreviewSection && (
            <InformationPreview
              handleChange={handleChangeInput}
              formData={formData}
            />
          )}
        </ModalBody>
        <ModalFooter className={styles.modal__container__footer}>
          {renderFooterContent()}
        </ModalFooter>
      </div>
    );
  };

  function mapFetchedDataToFields(data) {
    const { selectedForm } = data;

    setFormData((prev) => {
      const newFields = prev[selectedForm].fields.map((item) => ({
        ...item,
        value:
          item.id === 'mobileNumber'
            ? item.value || data[item.id]
            : data[item.id] || item.value,
      }));

      return {
        ...prev,
        selectedForm,
        [selectedForm]: {
          ...prev[selectedForm],
          checkedData: data,
          fields: data.notCompare ? checkForErrors(newFields, true) : newFields,
          notCompare: data.notCompare,
        },
      };
    });
  }

  // fetch payment info if it is empty and not fetched from server yet
  useEffect(() => {
    if (visible && isPaymentInfoEmpty) {
      dispatch(fetchPaymentInfoStart());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  useEffect(() => {
    if (paymentInfoData && !isPaymentInfoEmpty && visible) {
      mapFetchedDataToFields(paymentInfoData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentInfoData, isPaymentInfoEmpty, visible]);

  // updates state with mobile number value
  useEffect(() => {
    const [newPersonFields, newCompanyFields] = ['person', 'company'].map(
      (item) =>
        formData[item].fields.map((item) => {
          const value =
            item.id === 'mobileNumber' ? mobileNumValue : item.value;

          const disabled = item.id === 'mobileNumber' ? !!value : false;

          return {
            ...item,
            disabled,
            value,
          };
        })
    );

    setFormData((prev) => ({
      ...prev,
      person: {
        ...prev.person,
        fields: newPersonFields,
      },
      company: {
        ...prev.company,
        fields: newCompanyFields,
      },
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mobileNumValue]);

  if (showMobileVerfifyModal)
    return (
      <MobileVerificationModal
        mobile={inputMobileNumber}
        onValidate={changeMobileNumberIfEnteredCodeIsValid}
        show={showMobileVerfifyModal}
        onClose={handleCloseMobileVerifyModal}
      />
    );

  return (
    <>
      <Modal
        className={styles.user_info_modal}
        classNames={{ container: styles.modal__container }}
        visible={visible}
      >
        <ModalHeader
          showCloseBtn={showCancelBtn}
          handleCloseBtnClick={handleClose}
          title='فرم دریافت اطلاعات'
          className={styles.modal_header_container}
        >
          <MandatoryBadge
            show={showMandatoryBadge}
            selectedForm={formData.selectedForm}
          />
        </ModalHeader>
        {renderModalContent()}
      </Modal>
    </>
  );
}

export default UserInfoModal;
